import { Button, CircularProgress, Stack } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

import { deleteDocument, downloadDocuments } from "api";

import { EsgDeleteIcon, EsgDownloadIcon } from "assets";

import { DeleteDialog, useDialogControls } from "components";

import { useNotify } from "hooks";

import { QUERY_KEYS } from "consts";

import { useSetDefaultUploadedDocumentsSelection, useUserRole } from "recoils";

import { downloadFile, extractErrorMessage } from "utils";

export const EsgActions: React.FunctionComponent<{
    rowSelection: any;
    setrowSelection: any;
}> = ({ rowSelection, setrowSelection }) => {
    const notify = useNotify();
    const userRole = useUserRole();
    const queryClient = useQueryClient();
    const setDefaultSelection = useSetDefaultUploadedDocumentsSelection();
    const deleteDialogControls: any = useDialogControls();
    const { mutateAsync: deleteUploadedDocument } = useMutation(deleteDocument);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const selected: any[] = [];

    selected.push(...Object.keys(rowSelection));

    const onDeleteClick = () => {
        setIsDeleting(true);
        Promise.allSettled(
            selected.map((id) =>
                deleteUploadedDocument(id, {
                    onError: (error: any) => {
                        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : `Some error has happen while deleting documents: ${id}!`,)
                    },
                    onSuccess: () => {
                        notify.success("Selected documents were deleted!");
                        setrowSelection();
                        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
                    },
                }),
            ),
        ).finally(() => {
            setIsDeleting(false);
            deleteDialogControls.close();
            setDefaultSelection();
        });
    };

    const onDownloadClick = () => {
        setIsDownloading(true);
        downloadFile(() => downloadDocuments(selected), "documents.zip")
            .then(() => {
                notify.success("Selected documents were successfully downloaded!");
            })
            .catch((error) => {
                notify.error(extractErrorMessage(error, "Failed to download selected documents!"));
            })
            .finally(() => {
                setIsDownloading(false);
            });
    };

    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <Button
                startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <EsgDownloadIcon />}
                variant="contained"
                sx={{ borderRadius: "8px", width: '140px' }}
                disabled={!selected?.length || isDownloading}
                onClick={onDownloadClick}
            >
                {isDownloading ? "Downloading..." : "Download"}
            </Button>
            <Button
                startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <EsgDeleteIcon color="white" />}
                variant="contained"
                sx={{ borderRadius: "8px", width: '140px' }}
                disabled={!selected?.length || userRole.isAuditor || isDeleting}
                onClick={deleteDialogControls.open}
                color="error"
            >
                {isDeleting ? "Deleting..." : "Delete"}
            </Button>

            <DeleteDialog
                isOpen={deleteDialogControls.isOpen}
                onCancel={deleteDialogControls.close}
                onDeleteClick={onDeleteClick}
                selectedCount={selected.length}
                target="document"
                title="Delete documents?"
            />
        </Stack>
    );
};
